/**
 * The parked enemy jet (missions 3 and 9). Nose to the SOUTH at yaw 0, so the camera sees
 * the canopy, the dark intake mouths and the swept planform from above. 9 x 10.6 m, inside
 * the sim's 9 x 11 box. The outlines are exported so the wreck can reuse the same wing and fin.
 */

import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { type BoxColors, MeshBuilder, place } from './MeshBuilder';
import { mirrorX, prism } from './shapes';

/** Planform outlines, [x, z] in metres; +z is toward the nose. Starboard side; mirror for port. */
type Outline = ReadonlyArray<readonly [number, number]>;
export const JET_WING: Outline = [[0.65, 0.9], [4.5, -1.5], [4.5, -2.7], [0.65, -2.4]];
export const JET_TAILPLANE: Outline = [[0.6, -3.3], [2.3, -4.3], [2.3, -5.0], [0.6, -4.6]];
/** Fin outline as [height, z]: built in a frame rolled 90 degrees, see `uprightFrame`. */
export const JET_FIN: Outline = [[2.2, -2.6], [4.4, -4.4], [4.4, -5.2], [2.2, -4.7]];
/** The enemy's flash across the top of the fin: same leading and trailing edges, upper third only. */
export const JET_FIN_FLASH: Outline = [[3.7, -3.83], [4.4, -4.4], [4.4, -5.2], [3.7, -5.04]];

/** Under this transform a prism's outline x becomes height, and it is extruded across the aircraft. */
const uprightFrame = () => place(0, 0, 0, 0, 1, 0, Math.PI / 2);

export const aircraft: KitBuilder = (p: Palette) => {
  const mb = new MeshBuilder();
  const lo = p.color('metal.lo');
  const mid = p.color('metal.mid');
  const hi = p.color('metal.hi');
  const glass = p.color('ink.1');
  const accent = p.color('enemyAccent.0');
  const skin: BoxColors = { all: mid, top: hi, bottom: lo };

  // Undercarriage: she sits nose-high on three legs, belly a metre off the ground.
  mb.box(0.18, 1.0, 0.3, glass, place(0, 0, 3.0));
  for (const sx of [-1, 1]) mb.box(0.25, 1.0, 0.5, glass, place(sx * 1.2, 0, -1.4));

  // Fuselage, stepped down toward a dark radome; the tailpipe is the one hole in the back.
  mb.box(1.3, 1.25, 6.6, skin, place(0, 0.95, -0.9));
  mb.box(1.0, 1.05, 1.6, skin, place(0, 1.0, 3.2));
  mb.cylinder(0.52, 0, 1.4, 6, lo, place(0, 1.5, 4.0, 0, 1, Math.PI / 2));
  mb.cylinder(0.5, 0.42, 0.9, 8, glass, place(0, 1.55, -4.2, 0, 1, -Math.PI / 2), p.color('ink.0'));
  mb.box(0.5, 0.3, 3.4, skin, place(0, 2.2, -1.6)); // dorsal spine, running back into the fin

  // Cheek intakes: their black mouths face the camera and say "jet" more than anything else.
  for (const sx of [-1, 1]) mb.box(0.55, 0.85, 2.6, { ...skin, front: p.color('ink.0') }, place(sx * 0.92, 1.0, 0.6));

  // Canopy with one frame hoop.
  mb.box(0.78, 0.5, 1.9, glass, place(0, 2.05, 2.3));
  mb.box(0.84, 0.08, 0.16, mid, place(0, 2.5, 2.4));

  // Swept wings and tailplanes, with the enemy's bands across each wing (05 §4.3).
  const surface = { top: hi, side: lo };
  prism(mb, JET_WING, 1.3, 1.46, surface);
  prism(mb, mirrorX(JET_WING), 1.3, 1.46, surface);
  prism(mb, JET_TAILPLANE, 1.5, 1.62, surface);
  prism(mb, mirrorX(JET_TAILPLANE), 1.5, 1.62, surface);
  for (const sx of [-1, 1]) mb.box(0.8, 0.05, 1.3, accent, place(sx * 2.9, 1.45, -1.75));

  // Swept fin with the flash at its tip: visible edge-on from the south and as a blade from above.
  mb.with(uprightFrame(), () => {
    prism(mb, JET_FIN, -0.09, 0.09, { top: mid, side: lo, bottom: mid });
    prism(mb, JET_FIN_FLASH, -0.12, 0.12, { top: accent, side: accent });
  });
  return mb;
};
